import React, { useCallback, useEffect, useImperativeHandle, useMemo } from "react";
import { connect } from "react-redux";

import { faColumns } from "@fortawesome/pro-solid-svg-icons/faColumns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import {
  AnyEntity,
  EntityType,
  EntityTypesSupportingDownloading,
} from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, LoadingSpinner, Modal } from "@mapmycustomers/ui";

import DownloadEntitiesModal from "@app/component/DownloadEntitiesModal";
import Table from "@app/component/Table";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import ReportType from "@app/enum/ReportType";
import BaseModalPreviewProps from "@app/scene/reports/components/Overview/components/BaseModalPreviewProps";
import { DownloadConfirmation } from "@app/scene/reports/components/Overview/components/DownloadConfirmation";
import {
  downloadModalData,
  exportModalEntities,
  fetchModalPreviewData,
} from "@app/scene/reports/store/actions";
import { getDateRange, getOverviewData } from "@app/scene/reports/store/selectors";
import { EntityTypeSupportedInOverviewPreview } from "@app/scene/reports/types/EntityTypeSupportedInOverviewPreview";
import useUserIds from "@app/scene/reports/utils/useUserIds";
import { hasPermission } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { formatDate } from "@app/util/formatters";

import { DownloadWarning } from "./DownloadWarning";
import styles from "./EntityListModal.module.scss";
import { messages, modalTitleMessages, viewAllMessages } from "./messages";
import { ViewAllLinkMap } from "./ViewAllLink";

const DATE_FORMAT = "PP";

const FETCH_LIMIT = 25;

const DOWNLOAD_LIMIT = 10_000;

const reportMap: Record<EntityTypeSupportedInOverviewPreview, ReportType> = {
  [EntityType.ACTIVITY]: ReportType.ACTIVITY,
  [EntityType.COMPANY]: ReportType.COMPANIES,
  [EntityType.DEAL]: ReportType.DEALS,
  [EntityType.PERSON]: ReportType.PEOPLE,
  [EntityType.ROUTE]: ReportType.ROUTES,
};

export interface ModalRef {
  download: (reportType: ReportType) => void;
  reload: (limit?: number) => void;
}

interface Props extends BaseModalPreviewProps {
  canDownload?: boolean;
}

const EntityListModal = (
  {
    canDownload,
    columns,
    dateRange,
    downloadModalData,
    entityType,
    exportModalEntities,
    fetchModalPreviewData,
    onHide,
    onRow,
    overviewData,
    selectedUsers,
  }: Props & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps,
  ref?: React.ForwardedRef<ModalRef>
) => {
  const intl = useIntl();
  const [downloadModalVisible, showDownloadModal, hideDownloadModal] = useBoolean();

  const userIds: Array<User["id"]> = useUserIds(selectedUsers);

  const { modalPreviews } = overviewData;

  const needToSelectColumnsForDownloading = useMemo(
    () =>
      [EntityType.ACTIVITY, EntityType.COMPANY, EntityType.DEAL, EntityType.PERSON].includes(
        entityType
      ),
    [entityType]
  );

  const startTime = dateRange.startDate.getTime();
  const endTime = dateRange.endDate.getTime();

  const fetchTableData = useCallback(
    (limit = FETCH_LIMIT) => {
      fetchModalPreviewData({
        dateRange,
        entityType,
        limit,
        userIds,
      });
    },
    // disabled deps linting coz it fails to understand a complex expression with getTime()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startTime, endTime, fetchModalPreviewData, userIds, entityType]
  );

  const exportTableData = useCallback(
    (columns: ColumnModel) => {
      exportModalEntities({
        columns,
        dateRange,
        entityType: entityType as EntityTypesSupportingDownloading,
        userIds,
      });
    },
    // disabled deps linting coz it fails to understand a complex expression with getTime()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startTime, endTime, exportModalEntities, userIds, entityType]
  );

  const downloadTableData = useCallback(
    (reportType: ReportType) => {
      downloadModalData({
        dateRange,
        entityType,
        reportType,
        userIds,
      });
    },
    // disabled deps linting coz it fails to understand a complex expression with getTime()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startTime, endTime, downloadModalData, userIds, entityType]
  );

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  useImperativeHandle(ref, () => ({
    download: downloadTableData,
    reload: fetchTableData,
  }));

  const total = modalPreviews[entityType].total;

  const items = useMemo(
    () => modalPreviews[entityType].items.slice(0, FETCH_LIMIT),
    [entityType, modalPreviews]
  );

  const title = intl.formatMessage(modalTitleMessages[entityType]);

  const handleDownload = useCallback(
    () => downloadTableData(reportMap[entityType]),
    [downloadTableData, entityType]
  );

  const ViewAllEntityLink = ViewAllLinkMap[entityType];

  const DownloadConfirm = total > DOWNLOAD_LIMIT ? DownloadWarning : DownloadConfirmation;

  return (
    <>
      <Modal
        centered
        destroyOnClose
        footer={[
          <ViewAllEntityLink className={styles.viewAllLink} key="view">
            {intl.formatMessage(viewAllMessages[entityType])}
          </ViewAllEntityLink>,
          canDownload && needToSelectColumnsForDownloading ? (
            <Button
              icon={<FontAwesomeIcon icon={faColumns} />}
              onClick={showDownloadModal}
              type="primary"
            >
              {intl.formatMessage(messages.selectColumns)}
            </Button>
          ) : canDownload && items.length ? (
            <DownloadConfirm key="download" onConfirm={handleDownload}>
              <Button disabled={!items.length} loading={modalPreviews.downloading} type="primary">
                {intl.formatMessage(messages.downloadButton)}
              </Button>
            </DownloadConfirm>
          ) : (
            <Button disabled type="primary">
              {intl.formatMessage(messages.downloadButton)}
            </Button>
          ),
        ]}
        onCancel={onHide}
        subtitle={
          <span>
            {formatDate(dateRange.startDate, DATE_FORMAT)} —{" "}
            {formatDate(dateRange.endDate, DATE_FORMAT)}
          </span>
        }
        title={
          total > FETCH_LIMIT ? (
            <TextWithInfo
              info={intl.formatMessage({
                id: "reports.overview.previewListModal.title.hint",
                defaultMessage:
                  "To see all records for this period, click “Quick Export” to download report as CSV",
                description: "Hint for preview modal title on Overview page",
              })}
              placement="top"
            >
              {title} ({FETCH_LIMIT}/{total})
            </TextWithInfo>
          ) : (
            `${title} (${total})`
          )
        }
        visible
        width="clamp(300px, 82vw, 850px)"
      >
        {modalPreviews.loading ? (
          <LoadingSpinner />
        ) : (
          <Table<AnyEntity>
            clickableRows
            columns={columns}
            dataSource={items}
            noHover
            onRow={onRow}
            pagination={false}
            rowKey="id"
            scroll={{ y: 335 }}
          />
        )}
      </Modal>
      {downloadModalVisible && (
        <DownloadEntitiesModal
          entityType={entityType as EntityTypesSupportingDownloading}
          hideInformation
          onDownload={exportTableData}
          onHide={hideDownloadModal}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  canDownload: hasPermission(state)(PermissionName.EXPORT_DOWNLOAD),
  dateRange: getDateRange(state),
  overviewData: getOverviewData(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  downloadModalData: downloadModalData.request,
  exportModalEntities,
  fetchModalPreviewData: fetchModalPreviewData.request,
};

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(
  React.forwardRef(EntityListModal)
);
